import { useMedplumProfile } from '@medplum/react-hooks';
import type { Patient, Practitioner, RelatedPerson } from '@medplum/fhirtypes';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { EXTENSION_URLS } from '../features/rbac/constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PortalAccount {
  /** Patient id whose data should be loaded. */
  patientId: string;
  /** Display label, e.g. "My Portal — Emma Smith" or "Liam Jones". */
  label: string;
  /** True when this account is the authenticated user's own chart (not a related patient). */
  isSelf: boolean;
}

export interface PortalSubjectContextValue {
  /** The portal account in view (null for non-client users or when unresolved). */
  activeAccount: PortalAccount | null;
  /** Active portal subject patient id (convenience shorthand). */
  activePatientId: string | undefined;
}

const EMPTY: PortalSubjectContextValue = {
  activeAccount: null,
  activePatientId: undefined,
};

const PortalSubjectContext = createContext<PortalSubjectContextValue>(EMPTY);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPatientName(profile: Patient | Practitioner | RelatedPerson | undefined): string {
  if (!profile) return 'Unknown';
  const n = profile.name?.[0];
  if (n?.text) return n.text;
  const given = n?.given?.join(' ') ?? '';
  const family = n?.family ?? '';
  return `${given} ${family}`.trim() || 'Unknown';
}

function getPortalSubjectPatientRef(profile: Patient | undefined): string | undefined {
  const ext = profile?.extension?.find((e) => e.url === EXTENSION_URLS.portalSubjectPatient);
  const ref = ext?.valueReference?.reference;
  if (ref?.startsWith('Patient/')) {
    return ref.slice('Patient/'.length);
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * One portal account per login: the client's own chart, or the linked patient for a
 * guarantor-only or RelatedPerson login. Switching between related patients
 * (`portal-related-patient`) is out of MVP and not offered.
 */
export function resolvePortalAccount(
  profile: Patient | Practitioner | RelatedPerson | undefined,
): PortalAccount | null {
  if (!profile) return null;

  // TODO: For RelatedPerson with multiple patients, enumerate from
  // ProjectMembership.access[].parameter entries. See PRD_NON_CLIENT_RELATED_PERSONS_FHIR_MAPPING.md
  if (profile.resourceType === 'RelatedPerson') {
    const relatedPerson = profile as RelatedPerson;
    const patientRef = relatedPerson.patient?.reference;
    if (!patientRef) return null;
    return {
      patientId: patientRef.replace('Patient/', ''),
      label: relatedPerson.patient?.display ?? 'Patient',
      isSelf: false,
    };
  }

  if (profile.resourceType !== 'Patient') {
    return null;
  }

  const selfId = profile.id;
  const portalSubjectId = getPortalSubjectPatientRef(profile);

  if (portalSubjectId && selfId && portalSubjectId !== selfId) {
    // Guarantor-only proxy: the profile is a login-only Patient, portal-subject is the real patient
    const subjectDisplay = profile.extension
      ?.find((e) => e.url === EXTENSION_URLS.portalSubjectPatient)
      ?.valueReference?.display;

    return {
      patientId: portalSubjectId,
      label: subjectDisplay || 'Linked Patient',
      isSelf: false,
    };
  }

  if (selfId) {
    return {
      patientId: selfId,
      label: `My Portal — ${getPatientName(profile)}`,
      isSelf: true,
    };
  }

  return null;
}

export function PortalSubjectProvider({ children }: { children: ReactNode }): JSX.Element {
  const profile = useMedplumProfile() as Patient | Practitioner | RelatedPerson | undefined;

  const activeAccount = useMemo(() => resolvePortalAccount(profile), [profile]);

  const value = useMemo<PortalSubjectContextValue>(() => ({
    activeAccount,
    activePatientId: activeAccount?.patientId,
  }), [activeAccount]);

  return (
    <PortalSubjectContext.Provider value={value}>
      {children}
    </PortalSubjectContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePortalSubject(): PortalSubjectContextValue {
  return useContext(PortalSubjectContext);
}
